import { AlertCircle, Loader2, X } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import optionChainsV2Service, {
  OptionChainContract,
  OptionHistoricalPriceItem,
} from "../../../../services/optionChainsV2Service";
import { FactorEntry } from "../../../../services/productFactorsService";
import { formatNumberFixed } from "../../../../utils/numberUtils";

interface EssentialOptionPriceFactorModalProps {
  isOpen: boolean;
  onClose: () => void;
  conId: number;
  symbol: string;
}

interface PriceFactorRow {
  datetimeUtc: string;
  price: number | null;
  factor: number | null;
}

const PAGE_SIZE_WINDOW_DAYS = 10;

function normalizeToMinute(value: string): string {
  const date = new Date(value);
  date.setUTCSeconds(0, 0);
  return date.toISOString();
}

async function fetchAllPrices(conId: number, from: string, to: string): Promise<OptionHistoricalPriceItem[]> {
  const items: OptionHistoricalPriceItem[] = [];
  let pageNumber = 1;
  let totalPages = 1;

  while (pageNumber <= totalPages) {
    const response = await optionChainsV2Service.getOptionPricesPage(conId, pageNumber, from, to);
    items.push(...response.items);
    totalPages = response.totalPages;
    pageNumber += 1;
  }

  return items;
}

async function fetchAllFactors(conId: number, from: string, to: string): Promise<FactorEntry[]> {
  const items: FactorEntry[] = [];
  let pageNumber = 1;
  let totalPages = 1;

  while (pageNumber <= totalPages) {
    const response = await optionChainsV2Service.getOptionFactorsPage(conId, pageNumber, from, to);
    items.push(...response.items);
    totalPages = response.totalPages;
    pageNumber += 1;
  }

  return items;
}

const EssentialOptionPriceFactorModal: React.FC<EssentialOptionPriceFactorModalProps> = ({
  isOpen,
  onClose,
  conId,
  symbol,
}) => {
  const requestIdRef = useRef(0);
  const [prices, setPrices] = useState<OptionHistoricalPriceItem[]>([]);
  const [factors, setFactors] = useState<FactorEntry[]>([]);
  const [optionContract, setOptionContract] = useState<OptionChainContract | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    const to = new Date();
    const from = new Date(to.getTime() - PAGE_SIZE_WINDOW_DAYS * 24 * 60 * 60 * 1000);

    setLoading(true);
    setError(null);

    try {
      const [optionResult, priceItems, factorItems] = await Promise.all([
        optionChainsV2Service.findOptionContractsByConIds([conId]),
        fetchAllPrices(conId, from.toISOString(), to.toISOString()),
        fetchAllFactors(conId, from.toISOString(), to.toISOString()),
      ]);
      if (requestId !== requestIdRef.current) return;
      setOptionContract(optionResult.optionContracts[0] ?? null);
      setPrices(priceItems);
      setFactors(factorItems);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setPrices([]);
      setFactors([]);
      setOptionContract(null);
      setError(err instanceof Error ? err.message : "Failed to fetch option price and factor data");
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [conId]);

  useEffect(() => {
    if (isOpen) fetchData();
    return () => {
      requestIdRef.current += 1;
      setPrices([]);
      setFactors([]);
      setOptionContract(null);
      setError(null);
    };
  }, [fetchData, isOpen]);

  const rows = useMemo<PriceFactorRow[]>(() => {
    const priceMap = new Map(
      prices.map((item) => [normalizeToMinute(item.datetimeUtc), Number(item.close)])
    );
    const factorMap = new Map(
      factors.map((item) => [normalizeToMinute(item.t), Number(item.f)])
    );
    const datetimes = new Set([...priceMap.keys(), ...factorMap.keys()]);

    return [...datetimes]
      .sort((a, b) => b.localeCompare(a))
      .map((datetimeUtc) => ({
        datetimeUtc,
        price: priceMap.get(datetimeUtc) ?? null,
        factor: factorMap.get(datetimeUtc) ?? null,
      }));
  }, [factors, prices]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[80vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-rp-separator dark:bg-rp-surface-raised">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-rp-separator">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Option Prices and Factors</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {optionContract?.optionSymbol ?? symbol} ({conId}) - last 10 days
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close option prices and factors"
            className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-rp-surface"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-7 w-7 animate-spin text-rp-amber" />
            </div>
          ) : error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                <span>{error}</span>
              </div>
              <button
                type="button"
                onClick={fetchData}
                className="mt-3 rounded-lg bg-rp-amber px-3 py-1.5 font-semibold text-white transition-colors hover:bg-yellow-600"
              >
                Retry
              </button>
            </div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center text-sm text-gray-500 dark:text-gray-400">
              No price or factor data available for the last 10 days
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-rp-separator">
              <table className="w-full">
                <thead className="sticky top-0 bg-gray-50 dark:bg-rp-surface-sunken">
                  <tr className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    <th className="px-4 py-3 text-left">Datetime (UTC)</th>
                    <th className="px-4 py-3 text-right">Price</th>
                    <th className="px-4 py-3 text-right">Factor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white dark:divide-rp-separator dark:bg-rp-surface-raised">
                  {rows.map((row) => (
                    <tr key={row.datetimeUtc} className="hover:bg-gray-50 dark:hover:bg-rp-surface">
                      <td className="px-4 py-2 font-mono text-xs text-gray-700 dark:text-gray-300">
                        {row.datetimeUtc.replace("T", " ").replace(".000Z", " UTC")}
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-sm text-gray-900 dark:text-white">
                        {row.price === null ? "--" : formatNumberFixed(row.price, 2)}
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-sm text-gray-900 dark:text-white">
                        {row.factor === null ? "--" : formatNumberFixed(row.factor, 6)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EssentialOptionPriceFactorModal;
